/**
 * LCFPIProjectModals.js
 * In-page dark IC-style modals for New Project and Project Workspace.
 * v2: Fixed evidence quality display, assessment endpoint, report downloads.
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import {
  X, ChevronRight, ChevronLeft, Loader, CheckCircle2,
  Building2, FolderOpen, Leaf, DollarSign, Upload, Eye,
  BarChart2, FileText, Send, AlertCircle, Save, Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  AssessmentProgressTracker,
  AssessmentWizardDispatcher,
} from "./LCFPIAssessmentWorkflow";

// ── Safe number formatter — never returns undefined/NaN ──────────────────────
const safeNum = (v, fallback = null) => {
  const n = Number(v);
  return (!v && v !== 0) || isNaN(n) ? fallback : n;
};
const fmtScore = (v) => {
  const n = safeNum(v);
  if (n === null) return "—";
  return `${n.toFixed(0)}/100`;
};
const fmtPct = (v) => {
  const n = safeNum(v);
  if (n === null) return "—";
  return `${n.toFixed(0)}%`;
};

// ── Shared dark input styles ─────────────────────────────────────────────────
const INPUT_CLS = "w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-blue-500 transition-colors placeholder-slate-600";
const LABEL_CLS = "text-xs text-slate-400 mb-1 block font-medium";
const SECTION_CLS = "bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5";

function Field({ label, children }) {
  return <div><label className={LABEL_CLS}>{label}</label>{children}</div>;
}

// ── Report downloader ─────────────────────────────────────────────────────────
// Correct endpoint prefix: /api/lcfpi/pdf/...
const REPORT_DEFS = [
  { key:"investment-memo",     label:"Investment Memo",      desc:"Investor one-pager",         endpoint:(id) => `/lcfpi/pdf/investment-memo/${id}`,   filename:"InvMemo" },
  { key:"project-health",      label:"Project Health Report",desc:"Operational status & risk",   endpoint:(id) => `/lcfpi/pdf/project-health/${id}`,    filename:"Health" },
  { key:"submission-readiness",label:"Submission Readiness", desc:"DFI pre-check report",        endpoint:(id) => `/lcfpi/pdf/submission-readiness/${id}`,filename:"Readiness" },
  { key:"pdd-draft",           label:"PDD Draft",            desc:"Programme Design Document",   endpoint:(id) => `/lcfpi/pdf/pdd-draft/${id}`,         filename:"PDD" },
  { key:"eoi",                 label:"EOI Package",          desc:"Expression of Interest",      endpoint:(id) => `/lcfpi/eoi/${id}/generic`,           filename:"EOI" },
];

function OutputReportButtons({ projectId, project }) {
  const [downloading, setDownloading] = useState({});

  const downloadReport = async (def) => {
    if (downloading[def.key]) return;
    setDownloading(d => ({...d, [def.key]: true}));
    try {
      const token = localStorage.getItem("decarboniq_token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${baseUrl}/api${def.endpoint(projectId)}`;
      const resp = await fetch(url, { headers:{ Authorization:`Bearer ${token}` } });

      if (!resp.ok) {
        // Show the actual backend error
        let errMsg = `HTTP ${resp.status}`;
        try {
          const errData = await resp.json();
          errMsg = errData.detail || errData.message || errMsg;
        } catch {}
        if (resp.status === 403) {
          toast.error(`${def.label} blocked: ${errMsg}. Check project tier and evidence.`);
        } else if (resp.status === 404) {
          toast.error(`${def.label}: Project or template not found`);
        } else {
          toast.error(`${def.label}: ${errMsg.substring(0,100)}`);
        }
        return;
      }

      // Download the PDF
      const blob = await resp.blob();
      if (blob.size < 100) {
        toast.error(`${def.label}: Empty response — project may need assessment first`);
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `LCFPI-${def.filename}-${projectId}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${def.label} downloaded`);
    } catch (err) {
      toast.error(`${def.label}: Network error — ${err.message?.substring(0,80) || "check connection"}`);
    } finally {
      setDownloading(d => ({...d, [def.key]: false}));
    }
  };

  return (
    <div className="space-y-2.5">
      {REPORT_DEFS.map(def => {
        const isLoading = downloading[def.key];
        return (
          <button key={def.key}
            data-testid={`lcfpi-output-${def.key}`}
            onClick={() => downloadReport(def)}
            disabled={isLoading}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/40 hover:bg-slate-800 disabled:opacity-60 transition-all group text-left">
            <div>
              <p className="text-sm font-bold text-white">{def.label}</p>
              <p className="text-xs text-slate-500">{def.desc}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isLoading ? (
                <Loader size={13} className="animate-spin text-blue-400" />
              ) : (
                <Download size={13} className="text-slate-600 group-hover:text-slate-300 transition-colors" />
              )}
            </div>
          </button>
        );
      })}
      <p className="text-[10px] text-slate-700 mt-2 text-center">
        Reports use Run 58 approved values · WorkflowGate applied · Gating reason shown if blocked
      </p>
    </div>
  );
}


// ── New Project Modal — 6-step guided stepper ────────────────────────────────
const STEPS = [
  { id:1, label:"Organisation", icon:Building2 },
  { id:2, label:"Project",      icon:FolderOpen },
  { id:3, label:"Climate",      icon:Leaf },
  { id:4, label:"Finance",      icon:DollarSign },
  { id:5, label:"Evidence",     icon:Upload },
  { id:6, label:"Review",       icon:Eye },
];

const SECTORS = ["renewable_energy","waste_management","agriculture","transport","housing","manufacturing","water","health","other"];
const LGAS = ["Agege","Ajeromi-Ifelodun","Alimosho","Amuwo-Odofin","Apapa","Badagry","Epe","Eti-Osa","Ibeju-Lekki","Ifako-Ijaiye","Ikeja","Ikorodu","Ikoyi","Kosofe","Lagos Island","Lagos Mainland","Mushin","Ojo","Oshodi-Isolo","Shomolu","Surulere"];

export function NewProjectModal({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState(null);

  const [form, setForm] = useState({
    // Step 1 — Organisation
    org_name:"", cac_number:"", contact_person:"", contact_email:"",
    sector:"renewable_energy", lga:"Ikeja",
    // Step 2 — Project
    project_name:"", description:"", project_type:"", implementation_stage:"planning",
    timeline_months:24, funding_need_usd:"",
    // Step 3 — Climate
    energy_consumption_mwh:0, fuel_consumption_l:0, renewable_energy_pct:0,
    waste_generated_t:0, waste_diverted_pct:0, materials_recovered_t:0, recycled_input_pct:0,
    // Step 4 — Finance
    annual_revenue_range:"", ebitda_range:"", existing_debt_usd:0,
    funding_purpose:"", ta_needs:"",
    // Step 5 — Evidence
    has_cac_cert:false, has_financial_statements:false, has_feasibility_study:false,
    has_business_plan:false, has_env_approvals:false, has_esg_policy:false,
    evidence_notes:"",
  });

  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { ...form, status:"draft" };
      let resp;
      if (draftId) {
        resp = await client.put(`/lcfpi/projects/${draftId}`, payload);
      } else {
        resp = await client.post("/lcfpi/projects", payload);
        if (resp.data?.project_id) setDraftId(resp.data.project_id);
      }
      toast.success("Draft saved");
    } catch { toast.error("Could not save draft"); }
    finally { setSaving(false); }
  };

  const submitAndAssess = async () => {
    if (!form.org_name || !form.project_name) {
      toast.error("Organisation name and project name are required"); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, status:"submitted" };
      let projectId = draftId;
      if (!projectId) {
        const r = await client.post("/lcfpi/projects", payload);
        projectId = r.data?.project_id;
      } else {
        await client.put(`/lcfpi/projects/${projectId}`, payload);
      }
      // Trigger assessment
      await client.post(`/lcfpi/projects/${projectId}/assess`).catch(() => {});
      toast.success("Project submitted — assessment running");
      const newProj = await client.get(`/lcfpi/projects/${projectId}`).then(r => r.data).catch(() => payload);
      onSaved?.(newProj);
    } catch { toast.error("Submission failed — draft saved"); }
    finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 1) return form.org_name.trim().length > 0;
    if (step === 2) return form.project_name.trim().length > 0 && form.funding_need_usd > 0;
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:"#000000cc" }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="ml-auto h-full flex flex-col" style={{ width:"min(680px,100vw)", background:"#060d1a", borderLeft:"1px solid #1e293b" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div>
            <p className="text-sm font-black text-white">New Project</p>
            <p className="text-[10px] text-slate-500">Step {step} of 6 — {STEPS[step-1].label}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveDraft} disabled={saving}
              data-testid="lcfpi-save-draft-btn"
              className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-40"
              style={{background:"#1e293b",color:"#94a3b8",border:"1px solid #334155"}}>
              {saving ? <Loader size={10} className="animate-spin"/> : <Save size={10}/>} Save Draft
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800"><X size={16} className="text-slate-400"/></button>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex px-6 py-3 border-b border-slate-800 gap-1 flex-shrink-0">
          {STEPS.map(s => {
            const done = s.id < step; const active = s.id === step;
            const Icon = s.icon;
            return (
              <div key={s.id} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${done?"bg-green-600 text-white":active?"bg-blue-600 text-white":"bg-slate-800 text-slate-500"}`}>
                  {done ? <CheckCircle2 size={12}/> : <Icon size={11}/>}
                </div>
                <p className={`text-[9px] font-semibold ${active?"text-blue-400":done?"text-green-400":"text-slate-600"}`}>{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Organisation Profile</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Organisation Name *"><input value={form.org_name} onChange={e=>set("org_name",e.target.value)} className={INPUT_CLS} placeholder="ABC Recyclers Ltd"/></Field>
                <Field label="CAC / Registration Number"><input value={form.cac_number} onChange={e=>set("cac_number",e.target.value)} className={INPUT_CLS} placeholder="RC123456"/></Field>
                <Field label="Contact Person"><input value={form.contact_person} onChange={e=>set("contact_person",e.target.value)} className={INPUT_CLS} placeholder="John Adeyemi"/></Field>
                <Field label="Contact Email"><input value={form.contact_email} onChange={e=>set("contact_email",e.target.value)} className={INPUT_CLS} placeholder="j@abc.com" type="email"/></Field>
                <Field label="Sector">
                  <select value={form.sector} onChange={e=>set("sector",e.target.value)} className={INPUT_CLS}>
                    {SECTORS.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                  </select>
                </Field>
                <Field label="LGA">
                  <select value={form.lga} onChange={e=>set("lga",e.target.value)} className={INPUT_CLS}>
                    {LGAS.map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Project Profile</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Project Name *"><input value={form.project_name} onChange={e=>set("project_name",e.target.value)} className={INPUT_CLS} placeholder="Lagos Solar Initiative"/></Field>
                  <Field label="Funding Requirement (USD) *"><input value={form.funding_need_usd} onChange={e=>set("funding_need_usd",Number(e.target.value))} className={INPUT_CLS} type="number" placeholder="500000"/></Field>
                </div>
                <Field label="Description"><textarea value={form.description} onChange={e=>set("description",e.target.value)} className={INPUT_CLS} rows={3} placeholder="Brief description of the project…"/></Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Project Type"><input value={form.project_type} onChange={e=>set("project_type",e.target.value)} className={INPUT_CLS} placeholder="Solar PV"/></Field>
                  <Field label="Implementation Stage">
                    <select value={form.implementation_stage} onChange={e=>set("implementation_stage",e.target.value)} className={INPUT_CLS}>
                      {["concept","planning","design","construction","operational","expansion"].map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Timeline (months)"><input value={form.timeline_months} onChange={e=>set("timeline_months",Number(e.target.value))} className={INPUT_CLS} type="number" min={1}/></Field>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Climate & Circularity Data</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Energy Consumption (MWh/yr)"><input value={form.energy_consumption_mwh} onChange={e=>set("energy_consumption_mwh",Number(e.target.value))} className={INPUT_CLS} type="number"/></Field>
                <Field label="Fuel Consumption (litres/yr)"><input value={form.fuel_consumption_l} onChange={e=>set("fuel_consumption_l",Number(e.target.value))} className={INPUT_CLS} type="number"/></Field>
                <Field label="Renewable Energy (%)"><input value={form.renewable_energy_pct} onChange={e=>set("renewable_energy_pct",Number(e.target.value))} className={INPUT_CLS} type="number" min={0} max={100}/></Field>
                <Field label="Waste Generated (t/yr)"><input value={form.waste_generated_t} onChange={e=>set("waste_generated_t",Number(e.target.value))} className={INPUT_CLS} type="number"/></Field>
                <Field label="Waste Diverted (%)"><input value={form.waste_diverted_pct} onChange={e=>set("waste_diverted_pct",Number(e.target.value))} className={INPUT_CLS} type="number" min={0} max={100}/></Field>
                <Field label="Materials Recovered (t/yr)"><input value={form.materials_recovered_t} onChange={e=>set("materials_recovered_t",Number(e.target.value))} className={INPUT_CLS} type="number"/></Field>
                <Field label="Recycled Input (%)"><input value={form.recycled_input_pct} onChange={e=>set("recycled_input_pct",Number(e.target.value))} className={INPUT_CLS} type="number" min={0} max={100}/></Field>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Finance Profile</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Annual Revenue Range">
                  <select value={form.annual_revenue_range} onChange={e=>set("annual_revenue_range",e.target.value)} className={INPUT_CLS}>
                    {["","<1M","1M-5M","5M-20M","20M-100M",">100M"].map(r=><option key={r} value={r}>{r||"Select range"}</option>)}
                  </select>
                </Field>
                <Field label="EBITDA Range">
                  <select value={form.ebitda_range} onChange={e=>set("ebitda_range",e.target.value)} className={INPUT_CLS}>
                    {["","Negative","0-500K","500K-2M","2M-10M",">10M"].map(r=><option key={r} value={r}>{r||"Select range"}</option>)}
                  </select>
                </Field>
                <Field label="Existing Debt (USD)"><input value={form.existing_debt_usd} onChange={e=>set("existing_debt_usd",Number(e.target.value))} className={INPUT_CLS} type="number"/></Field>
                <Field label="Funding Purpose"><input value={form.funding_purpose} onChange={e=>set("funding_purpose",e.target.value)} className={INPUT_CLS} placeholder="Equipment, working capital…"/></Field>
                <div className="col-span-2">
                  <Field label="Technical Assistance Needs"><textarea value={form.ta_needs} onChange={e=>set("ta_needs",e.target.value)} className={INPUT_CLS} rows={2} placeholder="e.g. Feasibility study, financial modelling…"/></Field>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Evidence Declaration</p>
              <div className="space-y-2 mb-4">
                {[
                  ["has_cac_cert","CAC Certificate / Company Registration"],
                  ["has_financial_statements","Financial Statements (last 2 years)"],
                  ["has_feasibility_study","Feasibility Study"],
                  ["has_business_plan","Business Plan"],
                  ["has_env_approvals","Environmental Approvals / EIA"],
                  ["has_esg_policy","ESG Policy / Sustainability Statement"],
                ].map(([k,label]) => (
                  <label key={k} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700/40 cursor-pointer hover:bg-slate-800 transition-all">
                    <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="accent-blue-500 w-4 h-4"/>
                    <span className="text-sm text-slate-300">{label}</span>
                  </label>
                ))}
              </div>
              <Field label="Notes on evidence availability">
                <textarea value={form.evidence_notes} onChange={e=>set("evidence_notes",e.target.value)} className={INPUT_CLS} rows={2} placeholder="e.g. Financial statements available, pending notarisation…"/>
              </Field>
            </div>
          )}

          {step === 6 && (
            <div className={SECTION_CLS}>
              <p className="text-sm font-bold text-white mb-4">Review & Submit</p>
              <div className="space-y-2 text-sm">
                {[
                  ["Organisation",  form.org_name || "—"],
                  ["Sector",        form.sector.replace(/_/g," ")],
                  ["LGA",           form.lga],
                  ["Project",       form.project_name || "—"],
                  ["Stage",         form.implementation_stage],
                  ["Funding Need",  form.funding_need_usd ? `$${Number(form.funding_need_usd).toLocaleString()}` : "—"],
                  ["Evidence items",Object.keys(form).filter(k=>k.startsWith("has_")&&form[k]).length+" declared"],
                ].map(([l,v]) => (
                  <div key={l} className="flex justify-between px-4 py-2 rounded-lg bg-slate-800/40">
                    <span className="text-slate-500 text-xs">{l}</span>
                    <span className="text-slate-300 text-xs font-semibold">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 px-4 py-3 rounded-xl bg-blue-900/20 border border-blue-700/30">
                <p className="text-xs text-blue-300 leading-relaxed">
                  Submitting will run the Climate, Circularity, SDG, Finance Readiness, and Investor-Fit assessment automatically. Results appear on the Portfolio tab within moments.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800 flex-shrink-0">
          <button onClick={() => step > 1 ? setStep(s=>s-1) : onClose()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors">
            <ChevronLeft size={12}/> {step > 1 ? "Back" : "Cancel"}
          </button>

          {step < 6 ? (
            <button onClick={() => setStep(s=>s+1)} disabled={!canNext()}
              data-testid="lcfpi-newproj-next-btn"
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl disabled:opacity-40"
              style={{background:"#1d4ed8",color:"#fff"}}>
              Next <ChevronRight size={12}/>
            </button>
          ) : (
            <button onClick={submitAndAssess} disabled={submitting}
              data-testid="lcfpi-submit-assess-btn"
              className="flex items-center gap-1.5 text-xs font-bold px-5 py-2 rounded-xl disabled:opacity-40"
              style={{background:"linear-gradient(135deg,#1d4ed8,#059669)",color:"#fff"}}>
              {submitting ? <Loader size={11} className="animate-spin"/> : <Send size={11}/>}
              {submitting ? "Submitting…" : "Submit & Run Assessment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Project Workspace Modal — view/edit existing project ─────────────────────
const WORKSPACE_TABS = [
  { id:"workflow",     label:"Workflow",      icon:CheckCircle2 },
  { id:"overview",     label:"Overview",      icon:Eye },
  { id:"assessment",   label:"Assessment",    icon:BarChart2 },
  { id:"evidence",     label:"Evidence",      icon:Upload },
  { id:"readiness",    label:"Readiness",     icon:CheckCircle2 },
  { id:"outputs",      label:"Outputs",       icon:FileText },
];

export function ProjectWorkspaceModal({ projectId, onClose }) {
  const [wsTab,      setWsTab]      = useState("workflow");
  const [project,    setProject]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [assessing,  setAssessing]  = useState(false);
  const [activeWizard, setActiveWizard] = useState(null);
  const [workflowKey, setWorkflowKey] = useState(0); // increment to force workflow refresh

  useEffect(() => {
    if (!projectId) return;
    client.get(`/lcfpi/projects/${projectId}`)
      .then(r => setProject(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const reRunAssessment = async () => {
    setAssessing(true);
    try {
      // Correct endpoint: POST /lcfpi/assess/{project_id} (not /projects/{id}/assess)
      const r = await client.post(`/lcfpi/assess/${projectId}`, {});
      if (r.data?.status === "assessed" || r.data?.calc) {
        const calc = r.data.calc || {};
        // Update local project state with new scores from assessment
        setProject(prev => ({
          ...prev,
          climate_score:        safeNum(calc.climate_score) ?? prev?.climate_score,
          circularity_score:    safeNum(calc.circularity_score) ?? prev?.circularity_score,
          sdg_score:            safeNum(calc.sdg?.overall_sdg_score || calc.sdg_score) ?? prev?.sdg_score,
          readiness_score:      safeNum(calc.overall_score) ?? prev?.readiness_score,
          readiness_tier:       calc.tier || prev?.readiness_tier,
          finance_readiness_score: safeNum(calc.finance_readiness?.score || calc.finance_readiness) ?? prev?.finance_readiness_score,
          eco_label:            calc.eco_label || prev?.eco_label,
          assessment:           calc,
          assessment_version:   (prev?.assessment_version || 0) + 1,
        }));
        toast.success("Assessment complete — scores updated");
      } else {
        toast.error("Assessment returned unexpected response");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Unknown error";
      if (err?.response?.status === 422) {
        toast.error(`Missing required fields: ${msg}`);
      } else if (err?.response?.status === 404) {
        toast.error("Project not found — check project ID");
      } else {
        toast.error(`Assessment failed: ${msg.substring(0, 120)}`);
      }
    } finally {
      setAssessing(false);
    }
  };

  const p = project || {};
  const readinessTier = p.readiness_tier || "—";
  const tierColor = { "Tier 1": "#22c55e", "Tier 2": "#f59e0b", "Tier 3": "#ef4444" }[readinessTier] || "#64748b";

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:"#000000cc" }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="ml-auto h-full flex flex-col" style={{ width:"min(720px,100vw)", background:"#060d1a", borderLeft:"1px solid #1e293b" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background:tierColor, boxShadow:`0 0 6px ${tierColor}` }}/>
            <div>
              <p className="text-sm font-black text-white">{p.org_name || "Project Workspace"}</p>
              <p className="text-[10px] text-slate-500">{p.project_id || ""} · {readinessTier} · {p.sector?.replace(/_/g," ")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wsTab === "assessment" && (
              <button onClick={reRunAssessment} disabled={assessing}
                data-testid="lcfpi-rerun-assessment-btn"
                className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-40"
                style={{background:"#1e40af30",color:"#93c5fd",border:"1px solid #1e40af40"}}>
                {assessing ? <Loader size={10} className="animate-spin"/> : <BarChart2 size={10}/>} Re-run Assessment
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800"><X size={16} className="text-slate-400"/></button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex px-6 border-b border-slate-800 flex-shrink-0">
          {WORKSPACE_TABS.map(t => {
            const isActive = wsTab === t.id; const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`lcfpi-ws-tab-${t.id}`}
                onClick={() => setWsTab(t.id)}
                className="flex items-center gap-1.5 text-xs px-3 py-3 font-semibold transition-all"
                style={{ color:isActive?"#fff":"#4b5563", borderBottom:`2px solid ${isActive?"#3b82f6":"transparent"}` }}>
                <Icon size={11} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && <div className="flex justify-center py-16"><Loader size={18} className="animate-spin text-slate-500"/></div>}

          {/* ── WORKFLOW TAB ─────────────────────────────────────────────── */}
          {!loading && wsTab === "workflow" && (
            <div>
              <div className="mb-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Assessment Workflow
                </p>
                <p className="text-[10px] text-slate-500">
                  Complete each assessment inside DeCarbonIQ. Click Start on any pending step.
                </p>
              </div>
              <AssessmentProgressTracker
                key={workflowKey}
                projectId={projectId}
                onStartStep={step => setActiveWizard(step)}
              />
              {/* Wizard dispatcher — opens appropriate wizard */}
              {activeWizard && (
                <AssessmentWizardDispatcher
                  projectId={projectId}
                  stepId={activeWizard}
                  onClose={() => setActiveWizard(null)}
                  onComplete={() => {
                    setActiveWizard(null);
                    setWorkflowKey(k => k + 1); // Force AssessmentProgressTracker to reload
                    client.get(`/lcfpi/projects/${projectId}`).then(r => setProject(r.data)).catch(() => {});
                  }}
                />
              )}
            </div>
          )}

          {!loading && wsTab === "overview" && (
            <div className="space-y-4">
              <div className={SECTION_CLS}>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Project Summary</p>
                <div className="grid grid-cols-2 gap-y-3">
                  {[
                    ["Organisation", p.org_name],
                    ["LGA", p.lga],
                    ["Sector", p.sector?.replace(/_/g," ")],
                    ["Stage", p.implementation_stage],
                    ["Funding Need", p.funding_need_usd ? `$${Number(p.funding_need_usd).toLocaleString()}` : "—"],
                    ["Status", p.status],
                  ].map(([l,v]) => v ? (
                    <div key={l}><p className="text-[10px] text-slate-500">{l}</p><p className="text-sm text-white font-semibold">{v}</p></div>
                  ) : null)}
                </div>
              </div>
              {p.description && (
                <div className={SECTION_CLS}>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</p>
                  <p className="text-sm text-slate-300 leading-relaxed">{p.description}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["Readiness", safeNum(p.readiness_score), tierColor],
                  ["Climate",   safeNum(p.climate_score),   "#34d399"],
                  ["Trust",     p.trust_level || null,      "#94a3b8"],
                ].map(([lbl, val, col]) => (
                  <div key={lbl} className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 text-center">
                    <p className="text-2xl font-black mb-0.5" style={{ color: val===null ? "#475569" : col }}>
                      {val === null ? "—" : typeof val === "string" ? val : Number(val).toFixed(0)}
                    </p>
                    <p className="text-xs text-slate-400">{lbl}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && wsTab === "assessment" && (
            <div className="space-y-4">
              <div className={SECTION_CLS}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Assessment Results</p>
                  {p.readiness_tier && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background:`${({ "Tier 1":"#14532d40","Tier 2":"#78350f40","Tier 3":"#7f1d1d40" }[p.readiness_tier]||"#1e293b")}`, color:({ "Tier 1":"#22c55e","Tier 2":"#f59e0b","Tier 3":"#ef4444" }[p.readiness_tier]||"#94a3b8") }}>
                      {p.readiness_tier}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["Climate Score",     p.climate_score,       "#34d399"],
                    ["Circularity Score", p.circularity_score,   "#a78bfa"],
                    ["SDG Score",         p.sdg_score,           "#60a5fa"],
                    ["Finance Readiness", p.finance_readiness_score ?? p.readiness_score, "#f59e0b"],
                    ["Overall Readiness", p.readiness_score,     "#22c55e"],
                    ["Evidence Quality",  p.evidence_completeness,"#06b6d4"],
                  ].map(([l, rawVal, c]) => {
                    const display = fmtScore(rawVal);
                    return (
                      <div key={l} className="bg-slate-800/40 rounded-xl p-3">
                        <p className="text-lg font-black mb-0.5" style={{ color: display === "—" ? "#475569" : c }}>
                          {display}
                        </p>
                        <p className="text-xs text-slate-400">{l}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              {p.assessment?.gaps && p.assessment.gaps.length > 0 && (
                <div className={SECTION_CLS}>
                  <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">Improvement Gaps</p>
                  <div className="space-y-1.5">
                    {p.assessment.gaps.slice(0,5).map((g,i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-400">
                        <AlertCircle size={10} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        {typeof g === "string" ? g : g?.message || JSON.stringify(g)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {p.assessment?.recommended_pathway && (
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl px-4 py-3">
                  <p className="text-xs font-bold text-blue-300 mb-1">Recommended Carbon Pathway</p>
                  <p className="text-xs text-blue-200">{p.assessment.recommended_pathway}</p>
                </div>
              )}
            </div>
          )}

          {!loading && wsTab === "evidence" && (
            <div className="space-y-3">
              <div className={SECTION_CLS}>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Evidence Status</p>
                {[
                  ["CAC Certificate",       p.has_cac_cert],
                  ["Financial Statements",  p.has_financial_statements],
                  ["Feasibility Study",     p.has_feasibility_study],
                  ["Business Plan",         p.has_business_plan],
                  ["Environmental Approval",p.has_env_approvals],
                  ["ESG Policy",            p.has_esg_policy],
                ].map(([l,v]) => (
                  <div key={l} className="flex items-center justify-between py-2.5 border-b border-slate-800/50 last:border-0">
                    <span className="text-sm text-slate-300">{l}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v?"bg-green-900/40 text-green-400 border border-green-700/30":"bg-slate-800 text-slate-500"}`}>
                      {v ? "Declared" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl px-4 py-3">
                <p className="text-xs text-blue-300">Evidence uploads coming soon. Declare documents above to improve your Trust Level and Readiness Score.</p>
              </div>
            </div>
          )}

          {!loading && wsTab === "readiness" && (
            <div className="space-y-4">
              <div className={SECTION_CLS}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Readiness Dashboard</p>
                  <span className="text-lg font-black" style={{color:tierColor}}>{readinessTier}</span>
                </div>
                <div className="bg-slate-800/40 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 rounded-full bg-slate-700">
                      <div className="h-2 rounded-full transition-all" style={{width:`${p.readiness_score||0}%`, background:tierColor}}/>
                    </div>
                    <span className="text-sm font-black" style={{color:tierColor}}>{safeNum(p.readiness_score) !== null ? Number(p.readiness_score).toFixed(0) : "0"}/100</span>
                  </div>
                  <p className="text-xs text-slate-500">Readiness Score</p>
                </div>
                <div className="space-y-2">
                  {[
                    ["Finance Readiness", safeNum(p.finance_readiness_score) ?? 0, "#22c55e"],
                    ["Evidence Completeness", safeNum(p.evidence_completeness) ?? 0, "#3b82f6"],
                    ["Climate Assessment", safeNum(p.climate_score) ?? 0, "#34d399"],
                  ].map(([l,v,c]) => (
                    <div key={l} className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-36">{l}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-700">
                        <div className="h-1.5 rounded-full" style={{width:`${v}%`,background:c}}/>
                      </div>
                      <span className="text-xs text-slate-400 w-8 text-right">{Number(v).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && wsTab === "outputs" && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 mb-3">Generate institutional reports for this project</p>
              <OutputReportButtons projectId={projectId} project={p} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
